import React, { useState } from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import type { CascadeOptions, DepartmentAccountInfo, DepartmentDriverType } from '../../api';

interface CascadeOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  departmentName: string;
  driverType: DepartmentDriverType;
  accounts: DepartmentAccountInfo[];
  onConfirm: (options: CascadeOptions) => Promise<void>;
  isLoading?: boolean;
}

export function CascadeOptionsModal({
  isOpen,
  onClose,
  departmentName,
  driverType,
  accounts,
  onConfirm,
  isLoading = false,
}: CascadeOptionsModalProps) {
  const [options, setOptions] = useState<CascadeOptions>({
    clearAccountDrivers: false,
    clearAccountOverrides: false,
    clearAccountAdjustments: false,
    clearAccountNotes: false,
  });

  if (!isOpen) return null;

  // Count accounts with each type of data
  const accountsWithDrivers = accounts.filter(a => a.hasAccountDriver).length;
  const accountsWithOverrides = accounts.filter(a => a.hasOverrides).length;
  const accountsWithAdjustments = accounts.filter(a => a.hasAdjustments).length;
  const hasRoomsForecast = accounts.some(a => a.hasDailyValues);

  const hasAnyData = accountsWithDrivers > 0 || accountsWithOverrides > 0 || accountsWithAdjustments > 0 || hasRoomsForecast;

  const handleToggle = (key: keyof CascadeOptions) => {
    setOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleConfirm = async () => {
    await onConfirm(options);
  };

  return (
    <div className="fv3-scrim fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
      <div className="fv3-dialog bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="fv3-dialog-head flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="fv3-dialog-title text-lg font-semibold text-slate-800">
            Apply Department Driver
          </h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <p className="text-sm text-slate-600 mb-4">
            You're setting <span className="font-medium text-slate-800">{driverType}</span> as the
            driver for all accounts in <span className="font-medium text-slate-800">{departmentName}</span>.
          </p>

          {hasAnyData ? (
            <>
              {hasRoomsForecast && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-3">
                  <AlertTriangle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700">
                    <span className="font-semibold">Rooms Forecast active.</span> Setting a department driver will automatically clear all daily revenue values entered via the Rooms Forecast.
                  </p>
                </div>
              )}

              {(accountsWithDrivers > 0 || accountsWithOverrides > 0 || accountsWithAdjustments > 0) && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                  <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-700">
                    Some accounts have existing data that will conflict with the department driver.
                    Choose what to clear:
                  </p>
                </div>
              )}

              <div className="space-y-3">
                {accountsWithDrivers > 0 && (
                  <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={options.clearAccountDrivers}
                      onChange={() => handleToggle('clearAccountDrivers')}
                      className="w-4 h-4 border-slate-300 rounded"
                      style={{ accentColor: 'var(--lodge-orange-400)' }}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-700">
                        Clear account-level drivers
                      </div>
                      <div className="text-xs text-slate-500">
                        {accountsWithDrivers} account{accountsWithDrivers !== 1 ? 's' : ''} with custom drivers
                      </div>
                    </div>
                  </label>
                )}

                {accountsWithOverrides > 0 && (
                  <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={options.clearAccountOverrides}
                      onChange={() => handleToggle('clearAccountOverrides')}
                      className="w-4 h-4 border-slate-300 rounded"
                      style={{ accentColor: 'var(--lodge-orange-400)' }}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-700">
                        Clear manual overrides
                      </div>
                      <div className="text-xs text-slate-500">
                        {accountsWithOverrides} account{accountsWithOverrides !== 1 ? 's' : ''} with overrides
                      </div>
                    </div>
                  </label>
                )}

                {accountsWithAdjustments > 0 && (
                  <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={options.clearAccountAdjustments}
                      onChange={() => handleToggle('clearAccountAdjustments')}
                      className="w-4 h-4 border-slate-300 rounded"
                      style={{ accentColor: 'var(--lodge-orange-400)' }}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-700">
                        Clear adjustments
                      </div>
                      <div className="text-xs text-slate-500">
                        {accountsWithAdjustments} account{accountsWithAdjustments !== 1 ? 's' : ''} with adjustments
                      </div>
                    </div>
                  </label>
                )}
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-500">
              No existing account-level data will be affected. Accounts will inherit
              the department driver automatically.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="fv3-dialog-foot flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
            style={{ background: 'var(--lodge-orange-400)', color: '#ffffff', border: 'none', boxShadow: 'var(--shadow-orange)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)'; }}
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Applying...
              </>
            ) : (
              'Apply Driver'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CascadeOptionsModal;
